import { message } from "antd";
import axios from "axios";
import { codeMessage } from "../AdminPortal/utils/request";
import { USER_TYPES } from '../Constants/ApplicationConstants';
import { getRoleFromAuth, getUserType } from '../../utils/commonUtils';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import {RoutesLookup} from '../Routes/RoutesLookup';
import { PLATFORM_URL } from '../Constants/EnvironmentVariableProvider';

const redirectAgentToPlatform = () => {
  const returnPath = encodeURIComponent(window.location.pathname + window.location.search);
  window.location.href = `${PLATFORM_URL}/Rewards?returnPath=${returnPath}`;
};
const isTokenExpired = (token) => {
  if (!token) return true;
  try {
    const tokenPayload = JSON.parse(atob(token.split('.')[1]));
    const expirationTime = tokenPayload.exp * 1000; // Convert to milliseconds
    const issueTime = tokenPayload.iat * 1000; // Issue time in milliseconds
    const tokenLifetime = expirationTime - issueTime;
    const bufferTime = Math.min(tokenLifetime * 0.2, 10 * 60 * 1000); // 10% of lifetime or 5 minutes, whichever is smaller
    const currentTime = Date.now();
    return currentTime >= (expirationTime - bufferTime);
  } catch (error) {
    console.error('Token parsing error:', error);
    return true;
  }
};

const apiLocation = process.env.REACT_APP_BASE_URL;

if (!apiLocation) {
  throw new Error("REACT_APP_BASE_URL is not found");
}

axios.interceptors.response.use((response) => response, manageErrorConnection);

// Set up basic axios interceptor immediately when this module loads
let globalMsalInstance = null;
export const setMsalInstance = (instance) => {
  globalMsalInstance = instance;
};

// Basic interceptor that will be enhanced when MSAL instance is available
axios.interceptors.request.use(
  async (config) => {
    if (!config.headers["Authorization"]) {
      const idToken = localStorage.getItem('token');
      
      if (idToken) {
        try {
          const parsedToken = JSON.parse(idToken);
          config.headers["Authorization"] = "Bearer " + parsedToken;
          config.headers["session-token"] = parsedToken;
          axios.defaults.headers.common["Authorization"] = "Bearer " + parsedToken;
          axios.defaults.headers.common["session-token"] = parsedToken;
        } catch (error) {
          console.error('Error parsing token from localStorage:', error);
        }
      }
    }
    
    if (config.headers["session-token"] && isTokenExpired(config.headers["session-token"])) {
      const loginSource = localStorage.getItem('loginSource');
      if (loginSource === 'platform-sso') {
        redirectAgentToPlatform();
        return Promise.reject(new Error('Token expired, redirecting to Platform'));
      } else if (globalMsalInstance?.getActiveAccount()) {
        await setAxioAuth(globalMsalInstance);
      }
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

console.log(`API LOCATION IS ${apiLocation}`);

const initAxiosGlobalConfigs = (auth) => {
  if (auth && auth.result) {
    let loggedInUserId = 0;

    if (
      getUserType(getRoleFromAuth(auth)) === USER_TYPES.ADMIN
    ) {
      loggedInUserId = auth.result.ORAD_KeyID;
    } else {
      loggedInUserId = auth.result.ACAG_KeyID;
    }

    axios.defaults.baseURL = apiLocation;
    axios.defaults.headers.post["Content-Type"] = "application/json";
    axios.defaults.headers.common["logged-in-user-id"] = loggedInUserId;
  }
};
const refreshAccessToken = async (msalInstance, account) => {
  const accessTokenRequest = {
    scopes: [],
    account
  };
  
  try {
    // First try silent token acquisition
    const token = await msalInstance.acquireTokenSilent(accessTokenRequest);
    
    // Validate the new token
    if (token && token.idToken) {
      const tokenPayload = JSON.parse(atob(token.idToken.split('.')[1]));
      console.log('New token expiration:', new Date(tokenPayload.exp * 1000));
      return token;
    }
    return null;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      try {
        const popupToken = await msalInstance.acquireTokenPopup(accessTokenRequest);
        return popupToken;
      } catch (popupError) {
        console.error('Popup token acquisition failed:', popupError);
        return null;
      }
    }
    console.error('Token refresh failed:', error);
    return null;
  }
};
async function setAxioAuth(msalInstance) {
  const account = JSON.parse(localStorage.getItem('account'))
  const currentToken = JSON.parse(localStorage.getItem('token'));
  
  if (isTokenExpired(currentToken)) {
    msalInstance.setActiveAccount(account);
    const token = await refreshAccessToken(msalInstance, account);
    if (token) {
      const account = token.account;
      const idToken = token.idToken;
      localStorage.setItem('account', JSON.stringify(account));
      localStorage.setItem('token', JSON.stringify(idToken));
      msalInstance.setActiveAccount(account);
      axios.defaults.headers.common["Authorization"] = "Bearer " + idToken;
      axios.defaults.headers.common["session-token"] = idToken;
    }
  }
}
export function setAxiosInterceptors(msalInstance) {
  // This function is now handled by the basic interceptor above
  // Just update the global msalInstance reference
  globalMsalInstance = msalInstance;
}

export const clearAxiosAuth = () => {
  axios.defaults.headers.common["Authorization"] = "";
  axios.defaults.headers.common["session-token"] = "";
  axios.defaults.headers.common["logged-in-user-id"] = "";
}

export default initAxiosGlobalConfigs;

function manageErrorConnection(err) {
  if (err.response && err.response.status !== 400) {
    let errorMessage = codeMessage[err?.response?.status];
    if (err.response && err.response.status === 401) {
      if (window.location.pathname === RoutesLookup.SSO_LOGIN) {
        return Promise.reject(err);
      }
      const loginSource = localStorage.getItem('loginSource');
      if (loginSource === 'platform-sso') {
        redirectAgentToPlatform();
        console.log('401 Unauthorized - redirecting to Platform');
      } else {
        window.location.href = RoutesLookup.SESSION_TIMEOUT;
      }
    } else {
      message.error(errorMessage);
    }
    return Promise.reject(new Error(errorMessage));
  } else if (err.code === "ECONNREFUSED") {
    return "nevermind";
  } else {
    return Promise.reject(err);
  }
}
